/**
 * DemoteDialogBody — what the confirm dialog says, per plan outcome.
 *
 * Three shapes, because three different things are about to happen: a plain
 * demote that leaves the slot empty, a SWAP that also puts a queued path live
 * to hold the floor, or a refusal. The swap is the one worth spelling out — it
 * writes twice, and nobody should discover the second write afterwards.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import { ArrowRight } from 'lucide-react';
import type { DemoteDestination, DemotePlanResult } from '@trafficpulse/shared';

export const DEMOTE_COPY: Record<
  DemoteDestination,
  { title: string; desc: string; cta: string; destructive?: boolean }
> = {
  queue: {
    title: 'Move to queue',
    desc: 'comes off the tracker and returns to the queue line as a candidate for the next rotation. Its test history is preserved.',
    cta: 'Move to queue',
  },
  waiting: {
    title: 'Move to waiting',
    desc: 'comes off the tracker and is parked in Waiting — kept in the bank but never promoted until you move it to the queue.',
    cta: 'To waiting',
  },
  eliminated: {
    title: 'Eliminate',
    desc: 'comes off the tracker and is eliminated. It will not be promoted again unless you bring it back from the Eliminated tab.',
    cta: 'Eliminate',
    destructive: true,
  },
};

export interface DemoteDialogBodyProps {
  itemName: string;
  to: DemoteDestination;
  plan?: DemotePlanResult;
  loading: boolean;
  error: string | null;
}

const Swap: FC<{ itemName: string; plan: DemotePlanResult }> = ({ itemName, plan }) => (
  <>
    <p>
      {`Moving '${itemName}' would leave `}
      <span className="font-medium text-foreground">{plan.poolName}</span>
      {` at ${(plan.active ?? 1) - 1} of a minimum ${plan.minLive}.`}
    </p>
    <p className="mt-2 flex items-center gap-1.5 rounded-md border border-border bg-muted/30 px-2 py-1.5 text-foreground">
      <ArrowRight className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
      <span className="font-medium">{plan.cover?.itemName}</span>
      <span className="text-muted-foreground">
        goes live to cover it · #{plan.cover?.queuePosition} in queue
      </span>
    </p>
    <p className="mt-2 text-[12px] text-muted-foreground">
      Both happen in one action, replacement first, so the flow never drops
      below the minimum.
    </p>
  </>
);

const DemoteDialogBody: FC<DemoteDialogBodyProps> = ({
  itemName,
  to,
  plan,
  loading,
  error,
}) => {
  if (loading) return <p>Checking what this would do…</p>;
  if (error) return <p className="text-destructive">{error}</p>;
  if (plan?.mode === 'blocked') {
    return <p className="text-destructive">{plan.blockReason}</p>;
  }
  if (plan?.mode === 'needs-cover') return <Swap itemName={itemName} plan={plan} />;
  return (
    <>
      <p>{`'${itemName}' ${DEMOTE_COPY[to].desc}`}</p>
      <p className="mt-2 text-[12px] text-muted-foreground">
        The next rotation refills the slot.
      </p>
    </>
  );
};

export default DemoteDialogBody;
